<template>
  <card-dialog
    v-model="modalVisible"
    max-width="600px"
    :title="eventGroupForm.id ? $t('event_group.edit.dialog.header') : $t('event_group.new.dialog.header')"
  >
    <v-card-text
      class="pa-6"
      @dragenter.prevent="dragenter"
    >
      <validation-observer ref="validation">
        <v-select
          v-model="eventGroupForm.type"
          :items="typeOptions"
          :label="$t('event_group.type.title')"
          outlined
          dense
        />

        <v-select
          v-if="eventGroupForm.type === 'screening' && isFederation"
          v-model="eventGroupForm.subtype"
          :items="screeningSubtypeOptions"
          :label="$t('event_group.subtype.title')"
          :placeholder="$t('event_group.subtype.placeholder')"
          outlined
          dense
          clearable
        />

        <v-select
          v-if="isFederation"
          v-model="eventGroupForm.participant_level"
          :items="participantLevelOptions"
          :label="$t('event_group.participant_level.title')"
          :placeholder="$t('event_group.participant_level.placeholder')"
          outlined
          dense
          clearable
        />

        <validation-provider
          v-slot="{ errors }"
          key="eventGroupFormInputTitle"
          rules="required"
        >
          <v-text-field
            v-model="eventGroupForm.title"
            :error-messages="errors"
            :label="$t('text_input.title.title')"
            :placeholder="$t('text_input.title.placeholder')"
            outlined
          />
        </validation-provider>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-calendar-alt
          </v-icon> {{ $t('picker.date.tournament_duration.title') }}
        </subtitle>

        <v-row no-gutters>
          <v-col
            cols="6"
            class="pr-2"
          >
            <validation-provider
              v-slot="{ errors }"
              key="eventGroupFormInputStartDate"
              rules="required"
            >
              <v-menu
                v-model="dateFromActive"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="eventGroupForm.start_date ? $dayjs(eventGroupForm.start_date).format(getDateTimeFormatForUser('D MMM, YYYY')) : ''"
                    :error-messages="errors"
                    :label="$t('picker.date.select_start_date.title')"
                    outlined
                    dense
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="eventGroupForm.start_date"
                  :first-day-of-week="1"
                  no-title
                  @input="validateDateRange; dateFromActive = false"
                />
              </v-menu>
            </validation-provider>
          </v-col>
          <v-col
            cols="6"
            class="pl-2"
          >
            <validation-provider
              v-slot="{ errors }"
              key="eventGroupFormInputEndDate"
              rules="required"
            >
              <v-menu
                v-model="dateToActive"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="eventGroupForm.end_date ? $dayjs(eventGroupForm.end_date).format(getDateTimeFormatForUser('D MMM, YYYY')) : ''"
                    :error-messages="errors"
                    :label="$t('picker.date.select_end_date.title')"
                    outlined
                    dense
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="eventGroupForm.end_date"
                  :first-day-of-week="1"
                  no-title
                  @input="validateDateRange; dateToActive = false"
                />
              </v-menu>
            </validation-provider>
          </v-col>
        </v-row>

        <v-alert
          v-if="endBeforeStart"
          class="caption pa-2 mt-2"
          type="warning"
          outlined
          color="orange darken-2"
          icon="fal fa-exclamation-triangle"
        >
          {{ $t('tournament_details.date_range_alert') }}
        </v-alert>

        <div
          v-if="!eventGroupForm.id && $can('create-event-notes')"
          class="mb-6 mt-4"
        >
          <v-row class="mb-0">
            <v-col>
              <subtitle>
                <v-icon
                  left
                  small
                >
                  fal fa-list
                </v-icon> {{ $t('add_note.dialog.header') }}
              </subtitle>
            </v-col>
            <v-col
              v-if="!hasNoPlayers"
              cols="auto"
              class="pl-4"
            >
              <v-switch
                v-model="eventGroupForm.is_note_pinned"
                class="pa-0 ma-0"
                hide-details
                dense
                :label="$t('switch.pinned')"
              />
            </v-col>
            <v-col
              v-if="!hasNoPlayers"
              cols="auto"
              class="pl-4"
            >
              <v-switch
                v-model="eventGroupForm.is_player_visible"
                class="pa-0 ma-0"
                hide-details
                dense
                :label="$t('switch.visible_to_players')"
              />
            </v-col>
          </v-row>

          <div>
            <rich-text-editor v-model="eventGroupForm.note" />
          </div>
        </div>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-user-group
          </v-icon> {{ $t('add_new.team') }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          key="eventGroupFormInputTeams"
        >
          <v-autocomplete
            v-model="eventGroupForm.team_ids"
            outlined
            :search-input.sync="searchInputTeams"
            :items="teamSelectItems"
            item-value="id"
            item-text="name"
            dense
            multiple
            :error-messages="errors"
            :placeholder="$t('select_team.placeholder')"
          >
            <template v-slot:selection="{item}">
              <span class="d-inline-flex align-center mr-1"><team-color-dot :color="item.color" />{{ item.name }}</span>
            </template>
            <template v-slot:item="{item}">
              <team-color-dot :color="item.color" />{{ item.name }}
            </template>
          </v-autocomplete>
        </validation-provider>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-user
          </v-icon> {{ $t("dropdown.players.title") }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          key="eventGroupFormInputPlayers"
        >
          <v-autocomplete
            v-model="eventGroupForm.player_ids"
            outlined
            :search-input.sync="searchInputPlayers"
            :items="playersToSelect"
            item-value="id"
            item-text="name"
            dense
            multiple
            :chips="false"
            :error-messages="errors"
            clearable
            :placeholder="$t('dropdown.players.placeholder')"
          >
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 2"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 2">{{ item.first_name }} {{ item.last_name }}</span>
              <span
                v-else-if="index === 2"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
            <template v-slot:item="{ item, on }">
              <v-list-item-action>
                <v-simple-checkbox
                  :value="eventGroupForm.player_ids.indexOf(item.id) > -1"
                  :ripple="false"
                  v-on="on"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.last_name }}, {{ item.first_name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="item.teams && item.teams.length">{{ item.teams.map(t => t.short_name).join(', ') }}</span>
                  <span
                    v-if="item.player_positions && item.player_positions.length > 0"
                  > &middot; {{ item.player_positions.map(p => p.short_name).join(', ') }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </validation-provider>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-user
          </v-icon> {{ $t("eventDetails.reservations.coaches") }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          key="eventGroupFormInputCoaches"
        >
          <v-autocomplete
            v-model="eventGroupForm.coach_ids"
            outlined
            :search-input.sync="searchInputCoaches"
            :items="coaches.data"
            :loading="coaches.loading"
            item-value="id"
            :item-text="(coach) => coach.first_name + ' ' + coach.last_name"
            dense
            multiple
            clearable
            :error-messages="errors"
            :placeholder="$t('eventDetails.reservations.coaches.placeholder')"
          >
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 3"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 3">{{ item.first_name }} {{ item.last_name }}</span>
              <span
                v-else-if="index === 3"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
          </v-autocomplete>
        </validation-provider>

        <template v-if="$can('view-event-media') || $can('create-event-media')">
          <v-divider class="mb-6 mt-2" />

          <subtitle class="mb-3">
            <v-icon
              left
              small
            >
              fal fa-photo-video
            </v-icon> {{ $t('event_details.media') }}
          </subtitle>

          <div v-if="$can('create-event-media')">
            <v-overlay
              color="white"
              light
              :value="fileUpload.isDragging"
              :opacity="0.8"
              @dragleave.prevent="dragleave"
              @dragover.prevent="dragover"
              @drop.prevent="drop"
            >
              <div
                class="mb-4 text-center"
                style="pointer-events: none"
              >
                <v-icon
                  x-large
                  color="grey darken-4"
                >
                  fal fa-upload
                </v-icon>
              </div>
              <div
                class="grey--text text--darken-4"
                style="pointer-events: none"
              >
                {{ $t('drag_n_drop.instruction') }}
              </div>
            </v-overlay>

            <v-row
              align="center"
              class="mb-4"
            >
              <v-col class="text-body-2 grey--text">
                {{ $t('media_upload.instruction') }}
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="grey darken-2"
                  text
                  class="text-none"
                  @click="$refs.uploadFile.click()"
                >
                  {{ $t('button.browse') }}
                  <v-icon
                    right
                    small
                  >
                    fal fa-upload
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <input
              id="upload-file"
              ref="uploadFile"
              type="file"
              multiple
              style="display: none"
              accept="video/*,audio/*,image/*,.doc,.docx,.pptx,.xlsx,.xls,application/pdf"
              @input="handleFileChange"
            >
          </div>

          <div v-if="$can('view-event-media')">
            <v-row>
              <v-col
                v-for="(mediaItem, mediaItemIndex) in media"
                :key="mediaItem.id"
                cols="6"
              >
                <media-grid-item
                  :media-item="mediaItem"
                  class="media-grid-item"
                  :can-delete="$can('delete-event-media')"
                  @preview="preview"
                  @delete="mediaItem.id ? startMediaDelete(mediaItem) : media.splice(mediaItemIndex, 1)"
                />
              </v-col>
            </v-row>
          </div>
        </template>
      </validation-observer>
    </v-card-text>

    <v-card-actions class="pa-6 pt-0">
      <v-btn
        v-if="eventGroupForm.id && $can('edit-event')"
        color="error"
        text
        @click="initDelete()"
      >
        {{ $t('button.delete') }}
      </v-btn>
      <v-spacer />
      <v-btn
        text
        @click="modalVisible = false"
      >
        {{ $t('button.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit"
      >
        {{ $t('button.save') }}
      </v-btn>
    </v-card-actions>
    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteEventGroup()"
    />

    <media-viewer-dialog
      :visible.sync="mediaViewerDialog.visible"
      :media="media"
      :view-media-id="mediaViewerDialog.media ? mediaViewerDialog.media.id : undefined"
      :view-only="!$can('edit-event-media')"
      :can-delete="$can('delete-event-media')"
      @media:updated="media.splice(media.findIndex(m => m.id === $event.id), 1, $event)"
    />

    <delete-confirmation-dialog
      :visible.sync="deleteMedia.dialog"
      :submitting="deleteMedia.deleting"
      @confirmed="destroyMedia()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import TeamColorDot from '@/components/inputs/TeamColorDot.vue'
import { CoachApiService, EventGroupApiService, PlayerApiService } from '@/data/services'
import { MediaApiService } from '@/data/services/MediaApiService'
import { EventBus } from '@/plugins/bus'
import { cloneDeep, difference, intersection, union } from 'lodash'
import Subtitle from '@/components/helpers/Subtitle'
import dayjs from 'dayjs'
import _axios from '@/plugins/axios'
import RichTextEditor from '@/components/inputs/RichTextEditor.vue'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import MediaGridItem from '@/components/helpers/MediaGridItem.vue'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { getDateTimeFormatForUser } from '@/utils/helpers'

const emptyForm = {
  title: '',
  note: '',
  is_note_pinned: false,
  is_player_visible: false,
  type: 'tournament',
  subtype: null,
  participant_level: null,
  start_date: dayjs().format('YYYY-MM-DD'),
  end_date: dayjs().add(15, 'day').format('YYYY-MM-DD'),
  team_ids: [],
  player_ids: [],
  coach_ids: []
}

const EVENT_GROUP_TYPES = ['tournament', 'training_camp', 'diagnostics', 'screening']
const SCREENING_SUBTYPES = ['central', 'state']
const PARTICIPANT_LEVELS = ['club', 'regional_department', 'national']

export default {
  name: 'TournamentFormDialog',
  components: { RichTextEditor, Subtitle, CardDialog, DeleteConfirmationDialog, MediaGridItem, MediaViewerDialog, TeamColorDot },
  mixins: [deleteConfirmationMixin],
  props: {
    eventGroup: {
      type: Object,
      default: () => ({})
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dateToActive: false,
      dateFromActive: false,
      eventGroupForm: cloneDeep(emptyForm),
      submitting: false,
      searchInputTeams: '',
      searchInputPlayers: '',
      searchInputCoaches: '',
      suppressIdsWatch: false,
      media: [],
      fileUpload: {
        isDragging: false
      },
      mediaViewerDialog: {
        visible: false,
        media: undefined
      },
      deleteMedia: {
        media: undefined,
        dialog: false,
        deleting: false
      },
      players: {
        data: [],
        loading: false,
        eventPlayers: []
      },
      coaches: {
        data: [],
        loading: false
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    isFederation() {
      return this.user?.organisation_settings?.has_multi_team_players || false
    },
    hasNoPlayers() {
      return this.user?.organisation_settings?.has_no_players || false
    },
    typeOptions() {
      return EVENT_GROUP_TYPES.map(t => ({
        value: t,
        text: this.$t(`event_group.type.${t}`)
      }))
    },
    screeningSubtypeOptions() {
      return SCREENING_SUBTYPES.map(s => ({
        value: s,
        text: this.$t(`event_group.subtype.${s}`)
      }))
    },
    participantLevelOptions() {
      return PARTICIPANT_LEVELS.map(l => ({
        value: l,
        text: this.$t(`event_group.participant_level.${l}`)
      }))
    },
    endBeforeStart() {
      if (!this.eventGroupForm.start_date || !this.eventGroupForm.end_date) {
        return false
      }

      const startDate = dayjs(this.eventGroupForm.start_date)
      const endDate = dayjs(this.eventGroupForm.end_date)

      return endDate.isBefore(startDate)
    },
    teamSelectItems() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    playersToSelect() {
      return this.players.data.slice(0).sort((a, b) => {
        const aSelected = this.eventGroupForm.player_ids.indexOf(a.id) > -1
        const bSelected = this.eventGroupForm.player_ids.indexOf(b.id) > -1

        if (aSelected && !bSelected) return -1
        else if (!aSelected && bSelected) return 1

        return 0
      })
    }
  },
  watch: {
    'eventGroupForm.team_ids': function (newValue, oldValue) {
      this.searchInputTeams = ''

      if (this.suppressIdsWatch) return

      const addedTeamIds = difference(newValue, oldValue)
      const removedTeamIds = difference(oldValue, newValue)

      const removePlayerIds = this.playersToSelect.filter(p => (p.team_ids || []).some(tid => removedTeamIds.includes(tid)) && !(p.team_ids || []).some(tid => newValue.includes(tid))).map(p => p.id)
      const addPlayerIds = this.playersToSelect.filter(p => (p.team_ids || []).some(tid => addedTeamIds.includes(tid))).map(p => p.id)

      this.eventGroupForm.player_ids = union(difference(this.eventGroupForm.player_ids, removePlayerIds), addPlayerIds)

      const removeCoachIds = this.coaches.data.filter(c => intersection(c.teams.filter(t => t.is_coach).map(t => t.id), removedTeamIds).length > 0).map(c => c.id)
      const addCoachIds = this.coaches.data.filter(c => intersection(c.teams.filter(t => t.is_coach).map(t => t.id), addedTeamIds).length > 0).map(c => c.id)

      this.eventGroupForm.coach_ids = union(difference(this.eventGroupForm.coach_ids, removeCoachIds), addCoachIds)
    },
    'eventGroupForm.player_ids': function () {
      this.searchInputPlayers = ''
    },
    'eventGroupForm.coach_ids': function () {
      this.searchInputCoaches = ''
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_TOURNAMENT_FORM, (eventGroup) => {
      this.resetForm()

      if (eventGroup && eventGroup.id) {
        this.loadEventGroup(eventGroup.id)
      }

      this.fetchAllPlayers()
      this.fetchAllCoaches()
      this.modalVisible = true
    })

    EventBus.$on(this.$events.EDIT_EVENT_GROUP, (eventGroupId) => {
      this.resetForm()

      if (eventGroupId) {
        this.loadEventGroup(eventGroupId)
        this.fetchAllPlayers()
        this.fetchAllCoaches()
        this.modalVisible = true
      }
    })
  },
  methods: {
    getDateTimeFormatForUser,
    resetForm() {
      this.eventGroupForm = cloneDeep(emptyForm)
      this.searchInputTeams = ''
      this.searchInputPlayers = ''
      this.searchInputCoaches = ''
      this.suppressIdsWatch = false
      this.media = []
      this.fileUpload.isDragging = false
    },
    async loadEventGroup(id) {
      try {
        this.suppressIdsWatch = true

        const response = await EventGroupApiService.get(id)
        this.eventGroupForm = {
          id: response.data.id,
          title: response.data.title,
          note: '',
          is_note_pinned: false,
          is_player_visible: false,
          type: response.data.type ?? 'tournament',
          subtype: response.data.subtype ?? null,
          participant_level: response.data.participant_level ?? null,
          start_date: response.data.start_date,
          end_date: response.data.end_date,
          team_ids: response.data.teams ? response.data.teams.map(team => team.id) : [],
          player_ids: response.data.players ? response.data.players.map(player => player.id) : [],
          coach_ids: response.data.coaches ? response.data.coaches.map(coach => coach.id) : []
        }
        this.players.eventPlayers = response.data.players || []

        this.fetchMedia(id)

        this.$nextTick(() => { this.suppressIdsWatch = false })
      } catch (error) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('error.load_tournament'),
          color: 'error'
        })
      }
    },
    async fetchAllPlayers() {
      try {
        this.players.loading = true

        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC' })

        this.players.data = result.data
      } finally {
        this.players.loading = false
      }
    },
    async fetchAllCoaches() {
      try {
        this.coaches.loading = true

        const result = await CoachApiService.getPaginated({})

        this.coaches.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.coach_get.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.coaches.loading = false
      }
    },
    validateDateRange() {
      if (this.endBeforeStart) {
        this.eventGroupForm.end_date = this.eventGroupForm.start_date
      }
    },
    initDelete() {
      this.deleteConfirmationDialogVisible = true
    },
    async deleteEventGroup() {
      try {
        this.deleting = true

        await EventGroupApiService.delete(this.eventGroupForm.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('success.tournament_deleted'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false
        EventBus.$emit(this.$events.EVENT_GROUP_DELETED, this.eventGroupForm)
        this.modalVisible = false
      } catch (error) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('error.delete_tournament'),
          color: 'error'
        })
      } finally {
        this.deleting = false
      }
    },
    async submit() {
      const isValid = await this.$refs.validation.validate()

      if (!isValid) {
        return
      }

      this.submitting = true

      try {
        let response
        const formData = {
          title: this.eventGroupForm.title,
          type: this.eventGroupForm.type,
          subtype: this.eventGroupForm.type === 'screening' && this.isFederation ? this.eventGroupForm.subtype : null,
          participant_level: this.isFederation ? this.eventGroupForm.participant_level : null,
          start_date: this.eventGroupForm.start_date,
          end_date: this.eventGroupForm.end_date,
          team_ids: this.eventGroupForm.team_ids,
          player_ids: this.eventGroupForm.player_ids,
          coach_ids: this.eventGroupForm.coach_ids,
          media_ids: this.media.filter(m => m.id).map(m => m.id)
        }

        if (!this.eventGroupForm.id) {
          formData.note = this.eventGroupForm.note
          formData.is_note_pinned = this.eventGroupForm.is_note_pinned
          formData.is_player_visible = this.eventGroupForm.is_player_visible
        }

        if (this.eventGroupForm.id) {
          response = await EventGroupApiService.update(this.eventGroupForm.id, formData)
          EventBus.$emit(this.$events.EVENT_GROUP_UPDATED, response.data)
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('success.tournament_updated'),
            color: 'success'
          })
        } else {
          response = await EventGroupApiService.create(formData)
          EventBus.$emit(this.$events.EVENT_GROUP_CREATED, response.data)
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('success.tournament_created'),
            color: 'success'
          })
        }

        this.modalVisible = false
      } catch (error) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('error.save_tournament'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    },
    async fetchMedia(id) {
      try {
        const response = await EventGroupApiService.getMedia(id)
        this.media = response.data
      } catch (e) {

      }
    },
    async handleFileChange() {
      await this.$nextTick()

      const files = [...this.$refs.uploadFile.files]

      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'dkg', 'csv']

      for (const file of files) {
        const type = file.type
        const extension = file.name.split('.').pop()

        if (type.match('image.*') || type.match('video.*') || type.match('audio.*') || allowedExtensions.indexOf(extension) > -1) {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            upload: true
          })
        } else {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            error: 'File type not allowed'
          })
        }
      }

      this.processUpload()
    },
    async processUpload() {
      let formData

      for (const file of this.media.filter(m => !!m.upload)) {
        formData = new FormData()
        formData.append('file', file.file, file.name)

        _axios.post('media/upload', formData, {
          onUploadProgress: (e) => {
            file.progress = Number((e.loaded / e.total) * 100)
          }
        }).then(response => {
          file.upload = false

          Object.assign(file, response.data.media)
        }).catch(error => {
          if (error.response && error.response.status === 422) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              color: 'error',
              text: this.$t('alert.file_type_not_allowed.error')
            })

            file.error = this.$t('alert.file_type_not_allowed.error')

            return
          }

          this.$store.dispatch(this.$storeActions.ALERT, {
            color: 'error',
            text: this.$t('alert.uploading_file.error')
          })

          file.error = this.$t('alert.uploading_file.error')
        })
      }
    },
    preview(media) {
      this.mediaViewerDialog.visible = true
      this.mediaViewerDialog.media = media
    },
    dragover() {
      this.fileUpload.isDragging = true
    },
    dragleave() {
      this.fileUpload.isDragging = false
    },
    dragenter() {
      this.fileUpload.isDragging = true
    },
    drop(e) {
      this.$refs.uploadFile.files = e.dataTransfer.files
      this.handleFileChange()
      this.fileUpload.isDragging = false
    },
    startMediaDelete(media) {
      this.deleteMedia.media = media
      this.deleteMedia.dialog = true
    },
    async destroyMedia() {
      if (!this.deleteMedia.media) return

      const id = this.deleteMedia.media.id

      try {
        this.deleteMedia.deleting = true

        await MediaApiService.delete(id)

        const index = this.media.findIndex(m => m.id === id)

        if (index > -1) this.media.splice(index, 1)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })

        this.deleteMedia.dialog = false
      } catch (e) {

      } finally {
        this.deleteMedia.media = undefined
        this.deleteMedia.deleting = false
      }
    }
  }
}
</script>
